import { useTranslation } from 'next-i18next';

import { FormEvent, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIMask } from 'react-imask';
import { useDebounce } from 'react-use';

import { Box, GridItem, Heading, SimpleGrid, Text } from '@chakra-ui/react';

import { addYears } from 'date-fns/addYears';
import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from 'lodash/isEqual';

import { RegexConst } from '@nocowanie/core';
import { DatePicker, FormConsents, Input } from '@nocowanie/common-ui';

import { DATE_FORMAT } from '@app/consts';
import { environment } from '@app/environments/environment';
import {
    checkoutReservationSlice,
    commonUiStateSlice,
    useAppDispatch,
    useAppSelector,
} from '@app/state';

import { consents } from './checkout-consents.def';
import { CheckoutFormData, getSchema } from './checkout-form.def';
import { CheckoutFormProps } from './checkout-form.props';

export const CheckoutForm = ({
    id,
    creditCardRequired = false,
    guestAddressRequired = false,
    acceptedCreditCardsInfo,
    formConsentsRef,
    onFormSubmit,
}: CheckoutFormProps) => {
    const { t } = useTranslation('checkout', { keyPrefix: 'form' });
    const isMobileBrowser = useAppSelector(commonUiStateSlice.selectors.selectIsMobileVersion);
    const [checkoutFormData, setCheckoutFormData] = useState<CheckoutFormData>();
    const [checkoutIsValid, setCheckoutIsValid] = useState<boolean>(false);

    const { t: formTranslation } = useTranslation('checkout', { keyPrefix: 'form.inputs' });
    const { t: validationTranslation } = useTranslation('checkout', {
        keyPrefix: 'form.validationErrors',
    });
    const { formSchema, userDataSchema, consentSchema, creditCardSchema, isValidSchema } =
        getSchema(validationTranslation, creditCardRequired, guestAddressRequired);
    const dispatch = useAppDispatch();
    const formMethods = useForm<CheckoutFormData>({
        mode: 'onTouched',
        reValidateMode: 'onChange',
        resolver: yupResolver(formSchema) as any,
        resetOptions: {
            keepDirtyValues: true,
        },
    });
    const defaultPhonePrefix = '+48';
    const urls = environment.CONSENTS_URLS;
    const {
        register,
        watch,
        formState: { errors, isValid },
        setValue,
        getFieldState,
        handleSubmit,
    } = formMethods;

    const postalCodeInputRef = useRef<HTMLInputElement>(null);
    const { ref: postalCodeInternalRef, ...postalCodeProps } = register('postalCode');
    useImperativeHandle(postalCodeInternalRef, () => postalCodeInputRef.current);

    const phoneNumberInputRef = useRef<HTMLInputElement>(null);
    const { ref: phoneNumberInternalRef, ...phoneNumberProps } = register('phoneNumber');
    useImperativeHandle(phoneNumberInternalRef, () => phoneNumberInputRef.current);

    const postalCodeMask = useIMask(
        {
            mask: '00-000',
            lazy: true,
        },
        {
            onAccept: (value: string, _) => {
                setValue('postalCode', value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });
            },
            ref: postalCodeInputRef,
        },
    );

    const phoneNumberMask = useIMask(
        {
            lazy: true,
            mask: '+00 000 000 000',
        },
        {
            onAccept: (value: string, _) => {
                setValue('phoneNumber', value, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                });
            },
            ref: phoneNumberInputRef,
        },
    );

    const isFieldSubmitted = (fieldName: keyof CheckoutFormData): boolean => {
        return getFieldState(fieldName).isDirty;
    };

    useEffect(() => {
        const subscription = watch(value => {
            if (isEqual(value, checkoutFormData)) {
                return;
            }

            setCheckoutFormData(value as any);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    const viewConfig = useMemo(() => {
        return {
            gridColumns: {
                base: 1,
                md: 2,
            },
            colSpan: isMobileBrowser ? 1 : 2,
        };
    }, [isMobileBrowser]);

    useEffect(() => {
        if (isValid === checkoutIsValid) {
            return;
        }

        setCheckoutIsValid(isValid);
    }, [isValid]);

    useDebounce(
        () => {
            const isUserDataValid = isValidSchema(userDataSchema, checkoutFormData);
            const isConsentValid = isValidSchema(
                consentSchema,
                checkoutFormData?.consentCheckbox ?? {},
            );
            const isCreditCardValid = isValidSchema(creditCardSchema, checkoutFormData);

            dispatch(
                checkoutReservationSlice.actions.setFormData({
                    isValid: {
                        isCreditCardValid,
                        isUserDataValid,
                        isConsentValid,
                    },
                    ...checkoutFormData,
                }),
            );
        },
        250,
        [checkoutFormData, checkoutIsValid],
    );

    const onSubmit = handleSubmit((data:CheckoutFormData, event) => {
        if(onFormSubmit){
            onFormSubmit(data);
        }
        if(event) {
            event.preventDefault()
        }
    });

    return (
        <FormProvider {...formMethods}>
            <form
                noValidate
                id={id ?? undefined}
                onSubmit={onSubmit}
            >
                {creditCardRequired && (
                    <Box mb={10}>
                        <Heading as={'h3'} size={'md'} mb={1}>
                            {t('prepaymentHeader')}
                        </Heading>
                        <Text color={'gray.500'} mb={5}>
                            {t('creditCardLabel')}
                        </Text>

                        <SimpleGrid
                            rowGap={{ base: 6, md: 8 }}
                            columnGap={10}
                            columns={viewConfig.gridColumns}
                        >
                            <GridItem colSpan={viewConfig.colSpan} maxW={'32rem'}>
                                <Input
                                    id="creditCardNumber"
                                    label={`${formTranslation('creditCardNumber')}`}
                                    invalid={!!errors.creditCardNumber}
                                    required={true}
                                    isSubmitted={isFieldSubmitted('creditCardNumber')}
                                    errorMessage={errors.creditCardNumber?.message}
                                    inputProps={{
                                        placeholder: `${formTranslation('creditCardNumber')}`,
                                        ...register('creditCardNumber', {
                                            onChange: (event: FormEvent<HTMLInputElement>) => {
                                                // Remove non-numerical digits
                                                const { currentTarget } = event;
                                                const currentValue = currentTarget.value;
                                                const newValue = currentValue.replace(/\D/g, '');

                                                if (currentValue !== newValue) {
                                                    setValue('creditCardNumber', newValue);
                                                }
                                            },
                                        }),
                                        inputMode: 'numeric',
                                        maxLength: 19,
                                        minLength: 15,
                                        pattern: RegexConst.NUMBERS_ONLY.toString(),
                                    }}
                                />
                            </GridItem>

                            <GridItem colSpan={viewConfig.colSpan} maxW={'16rem'}>
                                <DatePicker
                                    displayedDateFormat={DATE_FORMAT.expirationDateFormat}
                                    selectedDate={null}
                                    onChange={(date, formattedDate) => {
                                        setValue('creditCardExpirationDate', formattedDate, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                            shouldTouch: true,
                                        });
                                    }}
                                    pickerProps={{
                                        dateFormat: DATE_FORMAT.expirationDateFormat,
                                        minDate: new Date(),
                                        maxDate: addYears(new Date(), 10),
                                        showMonthYearPicker: true,
                                        showFullMonthYearPicker: true,
                                        shouldCloseOnSelect: true,
                                        customInput: (
                                            <Input
                                                id="creditCardExpirationDate"
                                                label={`${formTranslation('creditCardExpirationDate')}`}
                                                invalid={!!errors.creditCardExpirationDate}
                                                required={true}
                                                isSubmitted={isFieldSubmitted(
                                                    'creditCardExpirationDate',
                                                )}
                                                errorMessage={
                                                    errors.creditCardExpirationDate?.message
                                                }
                                                readOnly={true}
                                                inputProps={{
                                                    ...register('creditCardExpirationDate'),
                                                    placeholder: 'MM/YY',
                                                    textTransform: 'capitalize',
                                                    minLength: 5,
                                                    maxLength: 5,
                                                    cursor: 'pointer',
                                                }}
                                            />
                                        ),
                                    }}
                                />
                            </GridItem>

                            <Input
                                id="creditCardFirstName"
                                label={`${formTranslation('creditCardFirstName')}`}
                                invalid={!!errors.creditCardFirstName}
                                required={true}
                                isSubmitted={isFieldSubmitted('creditCardFirstName')}
                                errorMessage={errors.creditCardFirstName?.message}
                                inputProps={{
                                    placeholder: `${formTranslation('creditCardFirstName')}`,
                                    ...register('creditCardFirstName'),
                                }}
                            />

                            <Input
                                id="creditCardLastName"
                                label={`${formTranslation('creditCardLastName')}`}
                                invalid={!!errors.creditCardLastName}
                                required={true}
                                isSubmitted={isFieldSubmitted('creditCardLastName')}
                                errorMessage={errors.creditCardLastName?.message}
                                inputProps={{
                                    placeholder: `${formTranslation('creditCardLastName')}`,
                                    ...register('creditCardLastName'),
                                }}
                            />
                        </SimpleGrid>

                        {acceptedCreditCardsInfo}
                    </Box>
                )}

                <Box>
                    <Heading as={'h3'} size={'md'} mb={1}>
                        {t('header')}
                    </Heading>
                    <Text color={'gray.500'} mb={5}>
                        {t('text')}
                    </Text>

                    <SimpleGrid
                        rowGap={{ base: 6, md: 8 }}
                        columnGap={10}
                        columns={viewConfig.gridColumns}
                    >
                        <Input
                            id="firstName"
                            label={`${formTranslation('firstName')}`}
                            invalid={!!errors.firstName}
                            required={true}
                            isSubmitted={isFieldSubmitted('firstName')}
                            errorMessage={errors.firstName?.message}
                            inputProps={{
                                placeholder: `${formTranslation('firstName')}`,
                                ...register('firstName'),
                            }}
                        />

                        <Input
                            id="lastName"
                            label={`${formTranslation('lastName')}`}
                            invalid={!!errors.lastName}
                            required={true}
                            isSubmitted={isFieldSubmitted('lastName')}
                            errorMessage={errors.lastName?.message}
                            inputProps={{
                                placeholder: `${formTranslation('lastName')}`,
                                ...register('lastName'),
                            }}
                        />

                        {guestAddressRequired && (
                            <>
                                <Input
                                    id="postalCode"
                                    label={`${formTranslation('postalCode')}`}
                                    invalid={!!errors.postalCode}
                                    required={true}
                                    isSubmitted={isFieldSubmitted('postalCode')}
                                    errorMessage={errors.postalCode?.message}
                                    ref={postalCodeMask.ref}
                                    inputProps={{
                                        placeholder: '__-___',
                                        ...postalCodeProps,
                                    }}
                                />

                                <Input
                                    id="settlement"
                                    label={`${formTranslation('settlement')}`}
                                    invalid={!!errors.settlement}
                                    required={true}
                                    isSubmitted={isFieldSubmitted('settlement')}
                                    errorMessage={errors.settlement?.message}
                                    inputProps={{
                                        placeholder: `${formTranslation('settlement')}`,
                                        ...register('settlement'),
                                    }}
                                />

                                <GridItem colSpan={viewConfig.colSpan}>
                                    <Input
                                        id="address"
                                        label={`${formTranslation('address')}`}
                                        invalid={!!errors.address}
                                        required={true}
                                        isSubmitted={isFieldSubmitted('address')}
                                        errorMessage={errors.address?.message}
                                        inputProps={{
                                            placeholder: `${formTranslation('address')}`,
                                            ...register('address'),
                                        }}
                                    />
                                </GridItem>
                            </>
                        )}

                        <Input
                            id="phoneNumber"
                            label={`${formTranslation('phoneNumber')}`}
                            invalid={!!errors.phoneNumber}
                            required={true}
                            isSubmitted={isFieldSubmitted('phoneNumber')}
                            errorMessage={errors.phoneNumber?.message}
                            ref={phoneNumberMask.ref}
                            inputProps={{
                                type: 'tel',
                                placeholder: '+48 ___ ___ ___',
                                ...phoneNumberProps,

                                // onClick: (e: React.MouseEvent<HTMLInputElement>) => {
                                //     const target = e.target as HTMLInputElement;

                                //     if (target.value === defaultPhonePrefix) {
                                //         // const refElement = phoneNumberMask.ref?.current as any;

                                //         // // Needed to move the cursor to the end (after inserting the defaultPhonePrefix value)
                                //         // refElement.maskRef.updateCursor(defaultPhonePrefix.length);
                                //     }
                                // },
                                // onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                                //     const target = e.target as HTMLInputElement;

                                //     target.value = target.value || defaultPhonePrefix;

                                //     if (target.value) {
                                //         // requestAnimationFrame(() => {
                                //         //     const refElement = phoneNumberMask.ref?.current as any;
                                //         //     refElement.maskRef.updateCursor(
                                //         //         target.value === defaultPhonePrefix
                                //         //             ? defaultPhonePrefix.length
                                //         //             : target.value.length,
                                //         //     );
                                //         // });
                                //     }
                                // },
                            }}
                        />

                        <Input
                            id="email"
                            label={`${formTranslation('email')}`}
                            invalid={!!errors.email}
                            required={true}
                            isSubmitted={isFieldSubmitted('email')}
                            errorMessage={errors.email?.message}
                            inputProps={{
                                type: 'email',
                                placeholder: `${formTranslation('email')}`,
                                ...register('email'),
                            }}
                        />
                    </SimpleGrid>
                </Box>

                <FormConsents
                    consents={consents}
                    translationKey="checkout"
                    mt={8}
                    urls={urls}
                    ref={formConsentsRef}
                />
            </form>
        </FormProvider>
    );
};
